 
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

/**
 * Create a Cloud Functions client + a case-aware `callable()` helper.
 *
 * @param {import('firebase/app').FirebaseApp} firebase
 * @param {object} [opts]
 * @param {string} [opts.customDomain] - Override the functions URL
 *   (used by education to route through the load balancer).
 * @returns {{ functions, callable, functionsCustomDomain }}
 */
export function createFunctions(firebase, { customDomain } = {}) {
    const functions = customDomain
        ? getFunctions(firebase, customDomain)
        : getFunctions(firebase);

    const isEmulator = import.meta.env.DEV;

    if (isEmulator) {
        const host = import.meta.env.VITE_EMULATOR_HOST || 'localhost';
        const port = Number(import.meta.env.VITE_EMULATOR_PORT_FUNCTIONS) || 5001;
        connectFunctionsEmulator(functions, host, port);
        // The Functions SDK hardcodes http:// in connectFunctionsEmulator,
        // so patch it to https:// when the page is served over HTTPS (Caddy-backed).
        if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
            functions.emulatorOrigin = `https://${host}:${port}`;
        }
    }

    /**
     * Wrap httpsCallable. Through the load balancer, Cloud Run service names
     * are lowercase, so we lowercase the function name in prod. The emulator
     * is case-sensitive and needs the original camelCase export name.
     */
    function callable(name) {
        const resolvedName = isEmulator ? name : name.toLowerCase();
        return httpsCallable(functions, resolvedName);
    }

    return { functions, callable, functionsCustomDomain: customDomain };
}
